import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DispositionButton } from './disposition-button';
import { EssentialItemButton } from './essential-item-button';
import {
  focusedItem,
  FONTS,
  SCREEN_PADDING,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  ListDisplaySize,
  useOrderGuideSettingsContext,
} from '@/contexts/order-guide-settings-context';
import { OrderableItemListItem } from './orderable-item-list-item';
import { OrderGuideListViewItem } from './order-guide-list-view-item';
import { Platform, StyleProp, Text, View, ViewStyle } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';

interface OrderGuideListItemProps {
  disableTextInput?: boolean;
  isFocused: boolean;
  itemStyleOverrides?: StyleProp<ViewStyle>;
  option: ItemListOption;
  setDSRToolingActionSheetItemMetadata?: (sku: ItemListOptionMetadata) => void;
  showTooltipOnMount?: boolean;
}

export const OrderGuideListItem: React.FC<OrderGuideListItemProps> = ({
  disableTextInput,
  isFocused,
  itemStyleOverrides,
  option,
  setDSRToolingActionSheetItemMetadata,
  showTooltipOnMount,
}) => {
  const rowVariantPackUUIDs = useMemo(
    () => option.metadata!.skus.map((sku) => sku.variantPackUUID),
    [option.metadata],
  );
  const quantityState = useItemQuantityState(rowVariantPackUUIDs);
  const { listDisplaySize } = useOrderGuideSettingsContext();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();

  const getOrderGuideItem = useGetOrderGuideItem();
  const { editItemNotes } = useUpdateOrderGuideItemContext();
  const [styles] = useStyles();

  const { showListView } = useShowListView();

  const orderGuideItem = getOrderGuideItem(option.metadata!);

  const updateQuantity: UpdateQuantityFn = (updateQuantityData) => {
    // Resolve metadata for the variant being updated so we use that variant's
    // request notes (multi-SKU items have different notes per Kit/Package/etc.)
    const orderGuideItemForVariant = getOrderGuideItem(
      option.metadata!.skus?.find(
        (sku) => sku.variantPackUUID === updateQuantityData.variantPackUUID,
      ) ?? option.metadata!,
    );
    const orderGuideNotes =
      orderGuideItemForVariant?.order_guide_restaurant?.request;

    let nextUpdateQuantityData = updateQuantityData;
    if (
      orderGuideNotes &&
      updateQuantityData.previousQuantity === 0 &&
      updateQuantityData.newQuantity > 0
    ) {
      // Include order guide notes if the item is being added to cart
      nextUpdateQuantityData = {
        ...updateQuantityData,
        variantPackDetails: {
          ...updateQuantityData.variantPackDetails,
          orderGuideNotes,
        },
      };
    }

    debouncedUpdateQuantity(nextUpdateQuantityData, { source: 'order_guide' });
  };

  const { isItemMinimized } = useUpdateOrderGuideItemContext();

  const isMinimized = isItemMinimized(option.metadata!);

  if (
    orderGuideItem?.order_guide_restaurant &&
    isMinimized &&
    customOrderGuideEditingEnabled
  ) {
    return (
      <View
        style={[
          styles.minimized,
          showListView ? styles.minimizedLarge : null,
          isFocused ? focusedItem() : null,
        ]}
      >
        <DispositionButton
          isMinimized={isMinimized}
          itemMetadata={option.metadata!}
        />
        <Text style={[TYPOGRAPHY.small, styles.minimizedTitle]}>
          {option.title}
        </Text>
      </View>
    );
  }

  const showCondensedView = listDisplaySize === ListDisplaySize.Condensed;

  if (showListView) {
    return (
      <OrderGuideListViewItem
        // focused states for OG
        isFocused={isFocused}
        isMinimized={isMinimized}
        option={option}
        setDSRToolingActionSheetItemMetadata={
          setDSRToolingActionSheetItemMetadata
        }
        showCondensedView={showCondensedView}
        showTooltipOnMount={showTooltipOnMount}
        updateQuantity={updateQuantity}
      />
    );
  }

  const renderEssentialItemButton = Platform.OS === 'web';
  const renderRightActionButtons =
    renderEssentialItemButton || customOrderGuideEditingEnabled;

  return (
    <OrderableItemListItem
      disableTextInput={disableTextInput}
      // showListView is desktop view and rendered above,
      // so we don't need to highlight the focused item on mobile
      isFocused={false}
      itemStyleOverrides={itemStyleOverrides}
      onNotePress={customOrderGuideEditingEnabled ? editItemNotes : undefined}
      option={option}
      quantityState={quantityState}
      renderLastOrderedMessage={!showCondensedView}
      renderParLevels={!showCondensedView}
      rightComponent={
        orderGuideItem && renderRightActionButtons ? (
          <View style={styles.disposition}>
            {renderEssentialItemButton ? (
              <EssentialItemButton itemMetadata={option.metadata!} />
            ) : null}
            {customOrderGuideEditingEnabled ? (
              <DispositionButton
                isMinimized={isMinimized}
                itemMetadata={option.metadata!}
              />
            ) : null}
          </View>
        ) : null
      }
      showListView={showListView}
      showSingleOption={false}
      showTooltipOnMount={showTooltipOnMount}
      source="order_guide_list"
      updateQuantity={updateQuantity}
    />
  );
};

const useStyles = createStyles((theme) => ({
  disposition: {
    flexDirection: 'row',
    gap: 4,
    paddingLeft: 8,
  },
  minimized: {
    ...shadow({
      color: theme.colors.black,
      elevation: 2,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 0,
    }),
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    flex: 1,
    flexDirection: 'row-reverse',
    gap: 8,
    justifyContent: 'space-between',
    margin: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  minimizedLarge: {
    flexDirection: 'row',
    marginHorizontal: SCREEN_PADDING,
  },
  minimizedTitle: {
    flex: 1,
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
}));
